import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, LogOut, Phone, MapPin, Briefcase, ChevronRight, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useCart }         from "../../context/CartContext";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import { useSettings }     from "../../context/SettingsContext";
import { api }             from "../../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import OtpLoginModal from "../OtpLoginModal";

// Human-readable category from Pinnacle itemTypeCode e.g. RIGHT_DOOR_MIRROR -> Right Door Mirror
const formatCategory = (code) => {
  if (!code) return "";
  return code.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};

const NAV_LINKS = [
  { to: "/",        label: "Home",          end: true },
  { to: "/about",   label: "About" },
  { to: "/quote",   label: "Request Quote" },
  { to: "/returns", label: "Returns" },
  { to: "/contact", label: "Contact" },
];

export default function StoreHeader() {
  const navigate = useNavigate();
  const { count } = useCart();
  const { user, openLoginModal, closeLoginModal, loginModalOpen, loginReturnPath, logout } = useCustomerAuth();
  const { settings } = useSettings();
  const [q, setQ]               = useState("");
  const [mobile, setMobile]     = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [pendingOffers, setPendingOffers] = useState(0);
  const shopTimer = useRef(null);

  // Check for counter offers awaiting customer response
  useEffect(() => {
    if (!user) { setPendingOffers(0); return; }
    api.get("/offers/mine").then(r => {
      const pending = (r.data.items || []).filter(o => o.status === "countered").length;
      setPendingOffers(pending);
    }).catch(() => {});
  }, [user]);

  // Real categories from actual Pinnacle inventory — top 21 by part count,
  // so the dropdown only ever links to categories that genuinely have stock.
  useEffect(() => {
    api.get("/products/facets").then((r) => {
      const cats = (r.data.categories || [])
        .filter((c) => c && c.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 21)
        .map((c) => [formatCategory(c.name), `/shop?category=${encodeURIComponent(c.name)}`]);
      setCategories(cats);
    }).catch(() => {});
  }, []);

  const col1 = categories.slice(0, 7);
  const col2 = categories.slice(7, 14);
  const col3 = categories.slice(14, 21);

  const phone  = settings.store_phone   || "0406 291 986";
  const phone2 = settings.store_phone_2 || "1800 650 650";
  const city   = settings.store_address || "Brisbane, QLD";
  const hours  = settings.business_hours || "Mon–Fri 7:00–4:30 · Sat 7:00–2:00";
  const logo   = settings.logo_url      || "/logo.png";

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
  };

  const openShop  = () => { clearTimeout(shopTimer.current); setShopOpen(true); };
  const closeShop = () => { shopTimer.current = setTimeout(() => setShopOpen(false), 150); };

  return (
    <header className="w-full sticky top-0 z-40 shadow-md" data-testid="store-header">

          {/* ── Top info strip ─────────────────────────────────────── */}
      <div className="bg-[#0b1a3b] text-white text-[11px]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-9">
          <div className="flex items-center gap-5">

            {phone2 && (
              <a href={`tel:${phone2.replace(/\s/g,"")}`} className="hidden sm:flex items-center gap-1.5 hover:text-[#e31e24] transition-colors font-medium">
                <Phone className="h-3 w-3" /> {phone2}
              </a>
            )}

            <a href={`tel:${phone.replace(/\s/g,"")}`} className="flex items-center gap-1.5 hover:text-[#e31e24] transition-colors font-medium">
              <Phone className="h-3 w-3" /> {phone}
            </a>
            <span className="hidden md:flex items-center gap-1.5 text-blue-200">
              <MapPin className="h-3 w-3" /> {city}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden lg:block font-mono text-[10px] uppercase tracking-widest text-blue-300">{hours}</span>
            <Link to="/b2b"
              className="group inline-flex items-center gap-1.5 h-9 px-3 bg-[#e31e24] hover:bg-[#b71820] transition-colors text-white font-bold uppercase tracking-wide text-[11px]">
              <Briefcase className="h-3 w-3" />
              <span className="hidden sm:inline">Register for Trade account</span>
              <span className="sm:hidden">Trade</span>
              <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* ── Row 2: Logo + Search + Capricorn + Auth ───────────────────── */}
      <div className="bg-white border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-[72px] flex items-center gap-4">
          <Link to="/" className="shrink-0" data-testid="store-logo">
            <img src={logo} alt="BCP Brisbane Car Parts" className="h-14 w-auto object-contain"
              onError={(e) => { e.target.src = "/logo.png"; }} />
          </Link>

          <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-xl">
            <div className="flex w-full border-2 border-zinc-300 hover:border-[#1e5bb8] focus-within:border-[#1e5bb8] rounded-sm overflow-hidden transition-colors">
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by part, SKU, Make/Model or OEM number"
                className="flex-1 pl-4 pr-2 h-12 text-[15px] bg-white outline-none text-[#0b1a3b] placeholder:text-zinc-400"
                data-testid="store-search" />
              <button type="submit"
                className="px-6 h-12 bg-[#e31e24] hover:bg-[#b71820] text-white font-heading font-black uppercase tracking-widest text-[13px] transition-colors shrink-0">
                Search
              </button>
            </div>
          </form>

          {/* Capricorn Preferred Supplier — prominent between search and sign in */}
          <div className="hidden lg:flex items-center shrink-0">
            <img
              src="/badges/capricorn-preferred-supplier.png"
              alt="Capricorn Preferred Supplier"
              className="h-12 w-auto object-contain"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-4 h-10 border-2 border-zinc-300 hover:border-[#1e5bb8] rounded-sm text-[13px] font-semibold text-[#0b1a3b] transition-colors whitespace-nowrap relative"
                    data-testid="store-account-btn">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
                    {pendingOffers > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ background: "#e31e24", fontSize: 9 }}>
                        {pendingOffers}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end"
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e4e8f0",
                    boxShadow: "0 4px 16px rgba(11,26,59,0.12)",
                    backdropFilter: "none",
                    WebkitBackdropFilter: "none",
                    borderRadius: "10px",
                    padding: "4px",
                    minWidth: "160px",
                  }}>
                  <DropdownMenuItem asChild
                    className="rounded-md text-[13px] font-medium px-3 py-2 cursor-pointer transition-colors"
                    style={{ color: "#0b1a3b" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f5f7fb"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <Link to="/account">My account</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild
                    className="rounded-md text-[13px] font-medium px-3 py-2 cursor-pointer transition-colors"
                    style={{ color: "#0b1a3b" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f5f7fb"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <Link to="/account/orders">My orders</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator style={{ background: "#eef2f8", margin: "4px 0" }} />
                  <DropdownMenuItem
                    onClick={logout}
                    className="rounded-md text-[13px] font-medium px-3 py-2 cursor-pointer transition-colors flex items-center gap-2"
                    style={{ color: "#e31e24" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fff5f5"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <LogOut className="h-3.5 w-3.5" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button onClick={() => openLoginModal()}
                className="flex items-center gap-2 px-4 h-10 border-2 border-zinc-300 hover:border-[#1e5bb8] rounded-sm text-[13px] font-semibold text-[#0b1a3b] transition-colors whitespace-nowrap"
                data-testid="store-login-btn">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Sign In</span>
              </button>
            )}

            {/* OTP Login Modal */}
            {loginModalOpen && (
              <OtpLoginModal
                onClose={closeLoginModal}
                returnPath={loginReturnPath}
              />
            )}

            <Link to="/cart" className="relative" data-testid="store-cart-btn">
              <div className="flex items-center gap-2 px-4 h-10 border-2 border-zinc-300 hover:border-[#1e5bb8] rounded-sm text-[13px] font-semibold text-[#0b1a3b] transition-colors whitespace-nowrap">
                <ShoppingCart className="h-4 w-4" />
                <span className="hidden sm:inline">Cart</span>
              </div>
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#e31e24] text-white rounded-full text-[10px] font-bold flex items-center justify-center"
                  data-testid="cart-count">{count}</span>
              )}
            </Link>

            <button className="md:hidden flex items-center justify-center w-10 h-10 border-2 border-zinc-300 rounded-sm"
              onClick={() => setMobile(!mobile)}>
              {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Row 3: Blue nav bar — 15px / semibold ──────────────────────── */}
      <div className="hidden md:block bg-[#0b1a3b]" data-testid="store-nav">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-11">
          <nav className="flex items-center h-full">

            {/* Shop Parts */}
            <div className="relative h-full flex items-center"
              onMouseEnter={openShop} onMouseLeave={closeShop}>
              <Link to="/shop"
                className="h-full px-4 text-[15px] font-heading font-semibold tracking-wide flex items-center gap-1.5 text-white hover:bg-[#e31e24]/20 border-b-2 border-transparent hover:border-[#e31e24] whitespace-nowrap transition-colors">
                Shop Parts <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${shopOpen ? "rotate-180" : ""}`} />
              </Link>
              {shopOpen && (
                <div className="absolute top-full left-0 bg-white shadow-2xl border-t-2 border-[#e31e24] z-50 min-w-[640px] p-6"
                  onMouseEnter={openShop} onMouseLeave={closeShop}>
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#e31e24] mb-4">Browse by part type</div>
                  <div className="grid grid-cols-3 gap-x-8">
                    {[col1, col2, col3].map((col, ci) => (
                      <div key={ci}>
                        {col.map(([label, href]) => (
                          <Link key={label} to={href} onClick={() => setShopOpen(false)}
                            className="block py-2 px-1 text-[14px] font-medium text-[#0b1a3b] hover:text-[#e31e24] hover:bg-red-50 border-b border-zinc-100 transition-colors">
                            {label}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap gap-3">
                    <Link to="/shop" onClick={() => setShopOpen(false)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#0b1a3b] text-white font-heading font-semibold uppercase tracking-wide text-[13px] hover:bg-[#e31e24] transition-colors">
                      View All Parts <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                    <Link to={`/shop?category=${encodeURIComponent("ENGINE,Engine")}`} onClick={() => setShopOpen(false)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#e31e24] text-white font-heading font-semibold uppercase tracking-wide text-[13px] hover:bg-[#0b1a3b] transition-colors">
                      Engine <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                    <Link to={`/shop?category=${encodeURIComponent("TRANS_GEARBOX")}`} onClick={() => setShopOpen(false)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e5bb8] text-white font-heading font-semibold uppercase tracking-wide text-[13px] hover:bg-[#0b1a3b] transition-colors">
                      Transmission <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end}
                className={({ isActive }) =>
                  `h-full px-4 text-[15px] font-heading font-semibold tracking-wide flex items-center whitespace-nowrap transition-colors border-b-2 ${
                    isActive
                      ? "border-[#e31e24] text-white bg-[#e31e24]/20"
                      : "border-transparent text-white hover:bg-[#e31e24]/20 hover:border-[#e31e24]/60"
                  }`
                }>
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* ── Right side: replaced Free Shipping with actual selling points ── */}
          <div className="hidden lg:flex items-center gap-3 text-[12px] text-white/80 font-body">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              Australia-Wide Delivery
            </span>
            <span className="text-white/30">·</span>
            <span>90-Day Warranty</span>
          </div>
        </div>
      </div>

      {/* ── Mobile nav ────────────────────────────────────────────────── */}
      {mobile && (
        <div className="md:hidden bg-white border-t border-zinc-100 shadow-lg">
          <form onSubmit={onSearch} className="px-4 py-3 border-b border-zinc-100">
            <div className="flex border-2 border-zinc-300 rounded-sm overflow-hidden">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parts…"
                className="flex-1 pl-3 h-11 text-sm outline-none" />
              <button type="submit" className="px-4 h-11 bg-[#e31e24] text-white text-xs font-bold uppercase">Search</button>
            </div>
          </form>
          <Link to="/shop" onClick={() => setMobile(false)}
            className="block px-5 py-3 text-[15px] font-heading font-semibold border-b border-zinc-100 text-[#0b1a3b]">
            Shop Parts
          </Link>
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setMobile(false)}
              className={({ isActive }) =>
                `block px-5 py-3 text-[15px] font-heading font-semibold border-b border-zinc-100 ${isActive ? "text-[#e31e24] bg-red-50" : "text-[#0b1a3b]"}`
              }>
              {l.label}
            </NavLink>
          ))}
          <div className="flex gap-2 p-3 bg-zinc-50 border-b border-zinc-100">
            <Link to={`/shop?category=${encodeURIComponent("ENGINE,Engine")}`} onClick={() => setMobile(false)}
              className="flex-1 text-center py-3 rounded-sm bg-[#e31e24] text-white text-[13px] font-heading font-semibold uppercase tracking-wide">
              Engine
            </Link>
            <Link to={`/shop?category=${encodeURIComponent("TRANS_GEARBOX")}`} onClick={() => setMobile(false)}
              className="flex-1 text-center py-3 rounded-sm bg-[#1e5bb8] text-white text-[13px] font-heading font-semibold uppercase tracking-wide">
              Transmission
            </Link>
          </div>
          {categories.slice(0, 8).length > 0 && (
            <div className="bg-zinc-50 border-b border-zinc-100">
              <div className="px-5 pt-3 pb-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#e31e24]">
                Popular Categories
              </div>
              {categories.slice(0, 8).map(([label, href]) => (
                <Link key={label} to={href} onClick={() => setMobile(false)}
                  className="block px-8 py-2.5 text-[13px] font-medium text-[#1e5bb8] border-t border-zinc-100 hover:bg-blue-50">
                  {label}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </header>
  );
}